import React, { useState } from 'react';
import { KeyRound, Mail, Shield, X } from 'lucide-react';
import Modal from '@/components/Modal';
import axios from 'axios';
import { config } from '@/config';
import { setSession } from '@/redux/actions/sessionAction';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearProgram } from '@/redux/actions/programAction';
import { setCustomer } from '@/redux/actions/customerAction';

interface OneTimePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    email: string;
}

const OneTimePasswordModal: React.FC<OneTimePasswordModalProps> = ({
    isOpen,
    onClose,
    email
}) => {
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleOneTimePassword = async (otp: string) => {
        const response = await axios.post(`${config.backendUrl}/validate-one-time-password`, {
            email,
            oneTimePassword: Number(otp)
        });
        console.log(response.data);
        const { sessionToken, email: responseEmail, _id } = response.data;
        const session = { sessionToken, email: responseEmail || email, _id: _id || null };
        dispatch(setSession(session));
        dispatch(setCustomer({ cardCode: null, name: null }));
        dispatch(clearProgram());
        navigate('/select-program');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!otp.trim()) return;

        setIsLoading(true);
        setError('');

        try {
            await handleOneTimePassword(otp);
            // If successful, the parent will handle navigation/closing
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Verification failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setOtp('');
        setError('');
        setIsLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal close={handleClose}>
            <div className="md:p-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                        <Shield className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                        Verify Your Identity
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                        We've sent a verification code to
                    </p>
                    <div className="flex items-center justify-center mt-2 p-2 bg-gray-50 rounded-lg">
                        <Mail className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">{email}</span>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="group">
                        <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-4 text-center">
                            Enter verification code
                        </label>
                        <div className="flex items-center justify-center p-6 rounded-xl border-2 border-gray-200 group-focus-within:border-blue-500 group-focus-within:ring-4 group-focus-within:ring-blue-500/10 transition-all duration-200 bg-gray-50 group-focus-within:bg-white">
                            <KeyRound className="h-6 w-6 text-gray-400 group-focus-within:text-blue-500 transition-colors mr-4" />
                            <input
                                id="otp"
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="flex-1 outline-none text-gray-900 placeholder-gray-500 bg-transparent text-center text-2xl font-semibold tracking-[0.3em] font-mono max-w-xs"
                                placeholder="123456"
                                maxLength={6}
                                autoComplete="one-time-code"
                                autoFocus
                                disabled={isLoading}
                                inputMode="numeric"
                                pattern="[0-9]*"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm text-center font-medium">
                                {error}
                            </p>
                        </div>
                    )}

                    <div className="space-y-3">
                        <button
                            type="submit"
                            disabled={!otp.trim() || isLoading}
                            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Verifying...</span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center space-x-2">
                                    <Shield className="h-5 w-5" />
                                    <span>Verify Code</span>
                                </div>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="w-full py-3 px-4 text-gray-600 font-medium rounded-xl hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-500/20 transition-all duration-200 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>

                {/* Help text */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center">
                        Didn't receive the code? Check your spam folder or contact support.
                    </p>
                </div>
            </div>
        </Modal>
    );
};

export default OneTimePasswordModal; 
